/**
 * useContextualPhoto — one hook, any page, real Unsplash imagery
 * with proper attribution.
 *
 * Hits /api/unsplash/search (server-side cached ~30 days). Returns a
 * ready-to-render photo object OR `null` while loading / on failure.
 * When `null`, callers fall back to a solid gradient — never a
 * broken image.
 */
import { useEffect, useState } from "react";
import { API } from "@/lib/api";


// ─── Internal helper: normalise Unsplash API payload → stable shape ──
function _normalisePhoto(p) {
  if (!p) return null;
  return {
    id:            p.photo_id,
    url:           p.image_url,
    thumb:         p.thumb_url,
    alt:           p.alt_description,
    color:         p.color,
    blur_hash:     p.blur_hash,
    credit_name:   p.photographer?.name,
    credit_url:    p.photographer?.profile_url,
    unsplash_url:  p.unsplash_link,
  };
}


export function useContextualPhoto(query, { orientation = "landscape" } = {}) {
  const [photo, setPhoto] = useState(null);

  useEffect(() => {
    if (!query) { setPhoto(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const url = `${API}/unsplash/search?q=${encodeURIComponent(query)}&per_page=1&orientation=${orientation}`;
        const r = await fetch(url);
        if (!r.ok) return;
        const body = await r.json();
        const first = (body.results || [])[0];
        if (!first || cancelled) return;
        const p = _normalisePhoto(first);
        if (p) setPhoto({ ...p, alt: p.alt || query });
      } catch { /* graceful fallback */ }
    })();
    return () => { cancelled = true; };
  }, [query, orientation]);

  return photo;
}


/**
 * useContextualPhotos — same as useContextualPhoto but returns an ARRAY
 * (default 3 photos). Same rate-cost as a single fetch — Unsplash returns
 * up to 30 photos per API call, and the server-side cache is keyed on
 * per_page so `count=3` caches separately from `count=1`.
 */
export function useContextualPhotos(query, { count = 3, orientation = "landscape" } = {}) {
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (!query) { setPhotos([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const url = `${API}/unsplash/search?q=${encodeURIComponent(query)}&per_page=${count}&orientation=${orientation}`;
        const r = await fetch(url);
        if (!r.ok) return;
        const body = await r.json();
        const list = (body.results || []).slice(0, count).map(_normalisePhoto).filter(Boolean);
        if (!cancelled) setPhotos(list.map(p => ({ ...p, alt: p.alt || query })));
      } catch { /* graceful fallback */ }
    })();
    return () => { cancelled = true; };
  }, [query, count, orientation]);

  return photos;
}

/**
 * <UnsplashCredit /> — renders the required "Photo by X on Unsplash"
 * attribution string. Drop wherever the photo is displayed.
 */
export function UnsplashCredit({ photo, className = "" }) {
  if (!photo?.credit_name) return null;
  return (
    <div className={`text-[10px] text-white/70 ${className}`} data-testid="unsplash-credit">
      Photo by{" "}
      <a href={photo.credit_url} target="_blank" rel="noreferrer" className="underline hover:text-white">
        {photo.credit_name}
      </a>
      {" "}on{" "}
      <a href={photo.unsplash_url} target="_blank" rel="noreferrer" className="underline hover:text-white">
        Unsplash
      </a>
    </div>
  );
}
